<template>
  <v-app
    v-if="placeTypes.length"
    data-qa="doctor-price-block-by-type"
  >
    <v-chip-group
      v-if="placeTypes.length > 1"
      v-model="activeType"
      mandatory
      class="mb-3"
      @change="handleChangeActiveType"
    >
      <v-chip
        v-for="placeType in placeTypes"
        :key="placeType"
        :data-place-type="placeType"
        :value="placeType"
        :color="placeType === activeType ? 'primary' : 'bg-primary'"
        :text-color="placeType === activeType ? 'bg-gray-0' : 'primary'"
      >
        {{ t(CHIP_LABELS[placeType], options) }}
      </v-chip>
    </v-chip-group>

    <DoctorPriceBlock
      ref="doctorPriceBlockRef"
      :page-type="activeType"
      :price="priceList[activeType].price"
      :club-price="activeType === 'clinic' ? priceList.clinic.clubPrice : undefined"
      :telemed-medtochka-price="activeType === 'telemed' ? priceList.telemed.medtochkaPrice : undefined"
      :tab-is-free-with-treatment="priceList.clinic.tabIsFreeWithTreatment"
      @update:redirect-value="handleUpdateRedirectValue"
    />
  </v-app>
</template>

<script setup lang="ts">
import I18NextVue from 'i18next'
import {
  computed,
  nextTick,
  onMounted,
  ref,
} from 'vue'
import { dispatchCustomEventGlobally } from 'www/utils'
import { apiScheduleTelemed } from 'www/api'
import { type RedirectValue } from 'www/constants'
import { CLUB_EVENTS } from 'www/vue_components/common/Club/constants'
import { PLACE_TYPES } from 'www/vue_components/common/DoctorPriceBlockV2/constants'
import { PlaceType } from 'www/vue_components/common/DoctorPriceBlockV2/types'
import DoctorPriceBlock from 'www/vue_components/common/DoctorPriceBlockV2/DoctorPriceBlock.vue'

const CHIP_LABELS = {
  clinic: 'В клинику',
  home: 'На дом',
  telemed: 'На онлайн-консультацию',
} as const

const props = defineProps<{
  doctorId: number
  priceList: {
    clinic: {
      price?: number | null
      clubPrice?: number | null
      tabIsFreeWithTreatment?: boolean
    }
    home: {
      price?: number | null
    }
    telemed: {
      price?: number | null
      medtochkaPrice?: number | null
    }
  }
}>()

const { t, language } = I18NextVue
const options = { ns: language }

const doctorPriceBlockRef = ref()

const placeTypes = computed(() => PLACE_TYPES.filter(placeType => (
  typeof props.priceList[placeType].price === 'number'
  || (placeType === 'telemed' && typeof props.priceList.telemed.medtochkaPrice === 'number')
)))

const activeType = ref<PlaceType>(placeTypes.value[0])
const redirectValue = ref<RedirectValue>('default')
const prevRedirectValue = ref(redirectValue.value)

function handleUpdateRedirectValue(newRedirectValue: RedirectValue) {
  prevRedirectValue.value = redirectValue.value
  redirectValue.value = newRedirectValue
}

const lpuLoyaltyProgramEl = ref<HTMLDivElement | null>(null)

onMounted(() => {
  lpuLoyaltyProgramEl.value = document.querySelector(`[data-lpu-loyalty-program-doctor-id="${props.doctorId}"]`)
})

async function handleChangeActiveType() {
  await nextTick() // Ждём выполнение handleUpdateRedirectValue

  if (lpuLoyaltyProgramEl.value) {
    lpuLoyaltyProgramEl.value.hidden = activeType.value !== 'clinic'
  }

  dispatchCustomEventGlobally(CLUB_EVENTS.clubAppointmentTypeSelected, {
    prevRedirectValue: prevRedirectValue.value,
    redirectValue: redirectValue.value,
    target: doctorPriceBlockRef.value?.$el,
    scheduleType: apiScheduleTelemed.getScheduleTypeV2({
      telemed: props.priceList.telemed.price,
      telemedMedtochka: props.priceList.telemed.medtochkaPrice,
    }),
  })
}
</script>

<style scoped lang="scss">
:deep(.v-chip--active::before) {
  opacity: 0;
}

:deep(.v-chip-group .v-slide-group__content) {
  padding: 0;
}
</style>
